import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, getAccessToken } from "../auth/AuthContext";
import { GUIDES } from "./catalog";
import { HelpOverlay } from "./HelpOverlay";
import { audienceFor, resolveGuide, type HelpAudienceState } from "./resolveGuide";
import { nextStep, prevStep } from "./tourMachine";
import { FIRST_RUN_TOUR_ID, isModalOpen, shouldOfferTour, tourOutcomeToRecord, type TourEndReason } from "./tourState";
import type { HelpDialKind, HelpGuide } from "./types";

/** How often to re-check whether the way is clear to offer the first-run tour — see the offer effect. */
const OFFER_RETRY_MS = 800;

interface HelpControllerValue {
  /**
   * The guide currently running, or null. Already RESOLVED against the audience — steps that don't apply
   * have been dropped — because the overlay counts "step n of guide.steps.length" straight off it.
   */
  activeGuide: HelpGuide | null;
  stepIndex: number;
  /**
   * The kind of dial currently open, or null anywhere else (dashboard, groups, settings). Set by the
   * page showing a dial; the launcher uses it to hide guides written for the other kind.
   */
  dialKind: HelpDialKind | null;
  /** Tell the launcher which kind of dial is open. Pass null when leaving a dial. */
  setDialKind: (kind: HelpDialKind | null) => void;
  /**
   * Tell the tours what this person can do on the dial they have open (KEY-535), so steps they couldn't
   * follow are dropped rather than taught. Pass null when leaving a dial.
   */
  setHelpAudience: (audience: HelpAudienceState | null) => void;
  /** Start a guide by id (no-op for an unknown id, or one that resolves to no steps). */
  start: (guideId: string) => void;
  next: () => void;
  back: () => void;
  stop: () => void;
}

const HelpContext = createContext<HelpControllerValue | null>(null);

/** Access the running guided-help tour. Must be used within <HelpProvider>. */
export function useHelp(): HelpControllerValue {
  const value = useContext(HelpContext);
  if (!value) {
    throw new Error("useHelp must be used within <HelpProvider>");
  }
  return value;
}

/**
 * Owns the running "Helpmeet" tour and renders its overlay. Mount it high in the tree so a guide can
 * spotlight controls on any page. Nothing is shown until `start(id)` is called — with one exception, the
 * first-run walkthrough (KEY-535), which offers itself once in a person's life the first time a dial finishes
 * loading in front of them. See the offer effect below for every condition that has to hold first.
 */
export function HelpProvider({ children }: { children: ReactNode }) {
  const [guide, setGuide] = useState<HelpGuide | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [dialKind, setDialKind] = useState<HelpDialKind | null>(null);
  const [audience, setHelpAudience] = useState<HelpAudienceState | null>(null);

  // Which tours this person is done with. `loaded` is the whole discipline of WorkspaceModeProvider repeated:
  // nothing acts before the server has answered, and a failed read leaves it false for ever, so the cost of
  // the endpoint being down is that nobody is ambushed — never that everybody is.
  const [tours, setTours] = useState<{ loaded: boolean; seen: Set<string> }>({ loaded: false, seen: new Set() });
  const askedRef = useRef(false);
  const offeredRef = useRef(false);

  // Read lazily, on the first dial that isn't a confined session, rather than on mount: a PIN or guest token
  // is refused /api/me/tours outright, and asking anyway would put a 403 in every kiosk's console.
  useEffect(() => {
    if (askedRef.current || !audience || audience.confined) {
      return;
    }
    const token = getAccessToken();
    if (!token) {
      return;
    }
    askedRef.current = true;
    let cancelled = false;
    api.tours(token)
      .then((rows) => {
        if (!cancelled) {
          setTours({ loaded: true, seen: new Set(rows.map((r) => r.id)) });
        }
      })
      .catch(() => {
        // Stays unloaded, so the tour is simply never offered this session. Silence is the safe failure.
      });
    return () => {
      cancelled = true;
    };
  }, [audience]);

  const start = useCallback(
    (guideId: string) => {
      const found = GUIDES.find((g) => g.id === guideId);
      if (!found) {
        return;
      }
      const resolved = resolveGuide(found, audienceFor(audience, dialKind));
      // A guide with nothing left to show isn't a guide: better no overlay than an empty one.
      if (resolved.steps.length === 0) {
        return;
      }
      setGuide(resolved);
      setStepIndex(0);
    },
    [audience, dialKind],
  );

  const endGuide = useCallback(
    (reason: TourEndReason) => {
      const outcome = tourOutcomeToRecord(guide?.id, reason);
      setGuide(null);
      setStepIndex(0);
      if (!outcome) {
        return;
      }
      // Remembered here as well as on the server, so a failed write costs at most one more offer on a later
      // load rather than a second one in this session — the stance `useWhatsNew` takes on a failed dismiss.
      setTours((current) => ({ loaded: current.loaded, seen: new Set(current.seen).add(FIRST_RUN_TOUR_ID) }));
      const token = getAccessToken();
      if (!token) {
        return;
      }
      void api.recordTour(token, FIRST_RUN_TOUR_ID, outcome).catch(() => {});
    },
    [guide],
  );

  const stop = useCallback(() => endGuide("stopped"), [endGuide]);

  const next = useCallback(() => {
    if (!guide) return;
    const to = nextStep(stepIndex, guide.steps.length);
    if (to === "done") {
      endGuide("finished");
    } else {
      setStepIndex(to);
    }
  }, [guide, stepIndex, endGuide]);

  const back = useCallback(() => {
    setStepIndex((i) => prevStep(i));
  }, []);

  // The auto-offer. Every clause of `shouldOfferTour` is a veto; this effect only decides WHEN to ask it.
  //
  // It retries on a timer while the answer is no, for one reason: with no backfill every existing user meets
  // this on their next dial load, and `useWhatsNew` auto-opens over every app load with anything unread. If
  // the check ran once and gave up, those people — the ones most likely to have unread release notes — would
  // be the ones who never saw the tour. The timer stops the moment it fires, and never starts at all unless
  // a dial is loaded and the tour is genuinely unseen.
  useEffect(() => {
    if (offeredRef.current) {
      return;
    }
    const tryOffer = () => {
      if (offeredRef.current) {
        return true;
      }
      const clear = shouldOfferTour({
        loaded: tours.loaded,
        seen: tours.seen.has(FIRST_RUN_TOUR_ID),
        dialReady: audience !== null && dialKind !== null,
        confined: audience?.confined ?? true,
        guideRunning: guide !== null,
        modalOpen: isModalOpen(),
        viewportWidth: window.innerWidth,
      });
      if (!clear) {
        return false;
      }
      offeredRef.current = true;
      start(FIRST_RUN_TOUR_ID);
      return true;
    };

    if (tryOffer()) {
      return;
    }
    const timer = setInterval(() => {
      if (tryOffer()) {
        clearInterval(timer);
      }
    }, OFFER_RETRY_MS);
    return () => clearInterval(timer);
  }, [tours, audience, dialKind, guide, start]);

  const value = useMemo<HelpControllerValue>(
    () => ({ activeGuide: guide, stepIndex, dialKind, setDialKind, setHelpAudience, start, next, back, stop }),
    [guide, stepIndex, dialKind, start, next, back, stop],
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      {guide && (
        <HelpOverlay guide={guide} stepIndex={stepIndex} onNext={next} onBack={back} onStop={stop} />
      )}
    </HelpContext.Provider>
  );
}
